// 阅读辅助功能实现

import type {
  Bookmark,
  ReadingNote,
  ReadingSettings
} from '@/types/reading';

import type { StorageManager, LocalStorageManager } from './storage';
import { ReadingEngine } from './readingEngine';

export interface ReadingTheme {
  id: string;
  name: string;
  backgroundColor: string;
  textColor: string;
  linkColor: string;
  borderColor: string;
  isDark: boolean;
}

export interface OfflineChapter {
  bookId: string;
  chapterId: string;
  content: string;
  downloadTime: Date;
  size: number; // bytes
}

export class ReadingAssistant {
  private storage: StorageManager;
  private localStorage: LocalStorageManager;
  private readingEngine: ReadingEngine;
  private currentSettings: ReadingSettings;
  private offlineStorage: Map<string, OfflineChapter> = new Map();

  constructor(
    storage: StorageManager,
    localStorage: LocalStorageManager,
    readingEngine: ReadingEngine
  ) {
    this.storage = storage;
    this.localStorage = localStorage;
    this.readingEngine = readingEngine;
    this.currentSettings = this.localStorage.getReadingSettings();
  }

  // ============================================================================
  // 阅读设置管理
  // ============================================================================

  getReadingSettings(): ReadingSettings {
    return { ...this.currentSettings };
  }

  async updateReadingSettings(settings: Partial<ReadingSettings>): Promise<void> {
    try {
      this.currentSettings = {
        ...this.currentSettings,
        ...settings
      };

      this.localStorage.saveReadingSettings(this.currentSettings);
      
      // 触发设置更新事件
      this.notifySettingsChanged();

    } catch (error) {
      console.error('Update reading settings failed:', error);
    }
  }

  async resetReadingSettings(): Promise<void> {
    try {
      const defaultSettings: ReadingSettings = {
        fontSize: 16,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        backgroundColor: '#ffffff',
        textColor: '#333333',
        lineHeight: 1.6,
        pageWidth: 800,
        nightMode: false,
        autoScroll: false,
        scrollSpeed: 1,
        pageAnimation: true
      };

      await this.updateReadingSettings(defaultSettings);
    } catch (error) {
      console.error('Reset reading settings failed:', error);
    }
  }

  getAvailableThemes(): ReadingTheme[] {
    return [
      {
        id: 'light',
        name: '明亮模式',
        backgroundColor: '#ffffff',
        textColor: '#333333',
        linkColor: '#007bff',
        borderColor: '#e0e0e0',
        isDark: false
      },
      {
        id: 'dark',
        name: '暗黑模式',
        backgroundColor: '#1a1a1a',
        textColor: '#e0e0e0',
        linkColor: '#4dabf7',
        borderColor: '#404040',
        isDark: true
      },
      {
        id: 'sepia',
        name: '护眼模式',
        backgroundColor: '#f7f3e9',
        textColor: '#5c4b37',
        linkColor: '#8b4513',
        borderColor: '#d4c4a8',
        isDark: false
      },
      {
        id: 'green',
        name: '绿色护眼',
        backgroundColor: '#e8f5e8',
        textColor: '#2d5016',
        linkColor: '#4a7c59',
        borderColor: '#c8e6c9',
        isDark: false
      }
    ];
  }

  async applyTheme(themeId: string): Promise<void> {
    try {
      const theme = this.getAvailableThemes().find(t => t.id === themeId);
      if (!theme) {
        return;
      }

      await this.updateReadingSettings({
        backgroundColor: theme.backgroundColor,
        textColor: theme.textColor,
        nightMode: theme.isDark
      });

    } catch (error) {
      console.error('Apply theme failed:', error);
    }
  }

  private notifySettingsChanged(): void {
    // 这里可以触发事件通知UI更新
    window.dispatchEvent(new CustomEvent('readingSettingsChanged', {
      detail: this.currentSettings
    }));
  }

  // ============================================================================
  // 书签功能
  // ============================================================================

  async addBookmark(
    bookId: string,
    chapterId: string,
    position: number,
    note?: string
  ): Promise<string> {
    try {
      const bookmark: Bookmark = {
        id: this.generateBookmarkId(),
        bookId,
        chapterId,
        position,
        note,
        createdTime: new Date()
      };

      // 获取章节标题
      const chapters = await this.readingEngine.getChapterList(bookId);
      const chapter = chapters.find(ch => ch.id === chapterId);
      if (chapter) {
        bookmark.chapterTitle = chapter.title;
      }

      await this.storage.saveUserData('bookmark', bookmark, bookId);
      
      return bookmark.id;

    } catch (error) {
      console.error('Add bookmark failed:', error);
      throw error;
    }
  }

  async getBookmarks(_bookId: string): Promise<Bookmark[]> {
    try {
      // 这里需要获取特定图书的所有书签
      // 当前存储接口不支持按bookId获取所有书签，需要扩展
      return [];
    } catch (error) {
      console.error('Get bookmarks failed:', error);
      return [];
    }
  }

  async deleteBookmark(_bookId: string, _bookmarkId: string): Promise<boolean> {
    try {
      // 这里需要删除特定的书签
      // 当前存储接口不支持按ID删除书签，需要扩展
      return true;
    } catch (error) {
      console.error('Delete bookmark failed:', error);
      return false;
    }
  }

  async updateBookmark(
    _bookId: string,
    _bookmarkId: string,
    _updates: Partial<Bookmark>
  ): Promise<boolean> {
    try {
      // 这里需要更新书签信息
      return true;
    } catch (error) {
      console.error('Update bookmark failed:', error);
      return false;
    }
  }

  // ============================================================================
  // 笔记功能
  // ============================================================================

  async addNote(
    bookId: string,
    chapterId: string,
    position: number,
    content: string
  ): Promise<string> {
    try {
      const note: ReadingNote = {
        id: this.generateNoteId(),
        bookId,
        chapterId,
        position,
        content,
        createdTime: new Date(),
        updatedTime: new Date()
      };

      await this.storage.saveUserData('note', note, bookId);
      
      return note.id;

    } catch (error) {
      console.error('Add note failed:', error);
      throw error;
    }
  }

  async getNotes(_bookId: string): Promise<ReadingNote[]> {
    try {
      // 这里需要获取特定图书的所有笔记
      return [];
    } catch (error) {
      console.error('Get notes failed:', error);
      return [];
    }
  }

  async updateNote(
    _bookId: string,
    _noteId: string,
    _content: string
  ): Promise<boolean> {
    try {
      // 这里需要更新笔记内容
      return true;
    } catch (error) {
      console.error('Update note failed:', error);
      return false;
    }
  }

  async deleteNote(_bookId: string, _noteId: string): Promise<boolean> {
    try {
      // 这里需要删除特定的笔记
      return true;
    } catch (error) {
      console.error('Delete note failed:', error);
      return false;
    }
  }

  async searchNotes(bookId: string, keyword: string): Promise<ReadingNote[]> {
    try {
      const allNotes = await this.getNotes(bookId);
      const keywordLower = keyword.toLowerCase();
      
      return allNotes.filter(note =>
        note.content.toLowerCase().includes(keywordLower)
      );
    } catch (error) {
      console.error('Search notes failed:', error);
      return [];
    }
  }

  // ============================================================================
  // 离线阅读支持
  // ============================================================================

  async downloadChapterForOffline(bookId: string, chapterId: string): Promise<boolean> {
    try {
      const content = await this.readingEngine.getChapterContent(bookId, chapterId);
      if (!content) {
        return false;
      }

      const offlineChapter: OfflineChapter = {
        bookId,
        chapterId,
        content: content.content,
        downloadTime: new Date(),
        size: new Blob([content.content]).size
      };

      // 存储到内存缓存
      const key = `${bookId}_${chapterId}`;
      this.offlineStorage.set(key, offlineChapter);

      // 存储到持久化缓存
      await this.storage.setCacheItem('chapter', key, offlineChapter, 24 * 7); // 7天

      return true;

    } catch (error) {
      console.error('Download chapter for offline failed:', error);
      return false;
    }
  }

  async downloadBookForOffline(bookId: string, maxChapters?: number): Promise<{
    success: boolean;
    downloadedChapters: number;
    totalChapters: number;
    failedChapters: string[];
  }> {
    try {
      const chapters = await this.readingEngine.getChapterList(bookId);
      const chaptersToDownload = maxChapters ? chapters.slice(0, maxChapters) : chapters;
      
      const results = await Promise.allSettled(
        chaptersToDownload.map(chapter => 
          this.downloadChapterForOffline(bookId, chapter.id)
        )
      );

      const downloadedChapters = results.filter(result => 
        result.status === 'fulfilled' && result.value
      ).length;

      const failedChapters = chaptersToDownload
        .filter((_, index) => 
          results[index].status === 'rejected' || 
          (results[index].status === 'fulfilled' && !(results[index] as any).value)
        )
        .map(chapter => chapter.id);

      return {
        success: failedChapters.length === 0,
        downloadedChapters,
        totalChapters: chaptersToDownload.length,
        failedChapters
      };

    } catch (error) {
      console.error('Download book for offline failed:', error);
      return {
        success: false,
        downloadedChapters: 0,
        totalChapters: 0,
        failedChapters: []
      };
    }
  }

  async getOfflineChapter(bookId: string, chapterId: string): Promise<string | null> {
    try {
      const key = `${bookId}_${chapterId}`;
      
      // 先从内存缓存获取
      const memoryChapter = this.offlineStorage.get(key);
      if (memoryChapter) {
        return memoryChapter.content;
      }

      // 从持久化缓存获取
      const cachedChapter = await this.storage.getCacheItem('chapter', key);
      if (cachedChapter) {
        // 加载到内存缓存
        this.offlineStorage.set(key, cachedChapter);
        return cachedChapter.content;
      }

      return null;

    } catch (error) {
      console.error('Get offline chapter failed:', error);
      return null;
    }
  }

  async isChapterOfflineAvailable(bookId: string, chapterId: string): Promise<boolean> {
    try {
      const content = await this.getOfflineChapter(bookId, chapterId);
      return content !== null;
    } catch (error) {
      console.error('Check chapter offline availability failed:', error);
      return false;
    }
  }

  async getOfflineChapters(bookId: string): Promise<OfflineChapter[]> {
    try {
      const offlineChapters: OfflineChapter[] = [];
      
      // 从内存缓存获取
      for (const [_key, chapter] of this.offlineStorage.entries()) {
        if (chapter.bookId === bookId) {
          offlineChapters.push(chapter);
        }
      }

      return offlineChapters;

    } catch (error) {
      console.error('Get offline chapters failed:', error);
      return [];
    }
  }

  async deleteOfflineChapter(bookId: string, chapterId: string): Promise<boolean> {
    try {
      const key = `${bookId}_${chapterId}`;
      
      // 从内存缓存删除
      this.offlineStorage.delete(key);
      
      // 从持久化缓存删除
      await this.storage.setCacheItem('chapter', key, null, 0);
      
      return true;

    } catch (error) {
      console.error('Delete offline chapter failed:', error);
      return false;
    }
  }

  async clearOfflineData(bookId?: string): Promise<boolean> {
    try {
      if (bookId) {
        // 清除特定图书的离线数据
        const keysToDelete: string[] = [];
        
        for (const [key, chapter] of this.offlineStorage.entries()) {
          if (chapter.bookId === bookId) {
            keysToDelete.push(key);
          }
        }

        for (const key of keysToDelete) {
          this.offlineStorage.delete(key);
        }
      } else {
        // 清除所有离线数据
        this.offlineStorage.clear();
      }

      // 清除持久化缓存
      await this.storage.clearCacheByType('chapter');
      
      return true;

    } catch (error) {
      console.error('Clear offline data failed:', error);
      return false;
    }
  }

  async getOfflineStorageStats(): Promise<{
    totalChapters: number;
    totalSize: number; // bytes
    bookCount: number;
    oldestDownload: Date | null;
    newestDownload: Date | null;
  }> {
    try {
      const chapters = Array.from(this.offlineStorage.values());
      const bookIds = new Set(chapters.map(ch => ch.bookId));
      
      let totalSize = 0;
      let oldestDownload: Date | null = null;
      let newestDownload: Date | null = null;

      chapters.forEach(chapter => {
        totalSize += chapter.size;
        
        if (!oldestDownload || chapter.downloadTime < oldestDownload) {
          oldestDownload = chapter.downloadTime;
        }
        
        if (!newestDownload || chapter.downloadTime > newestDownload) {
          newestDownload = chapter.downloadTime;
        }
      });

      return {
        totalChapters: chapters.length,
        totalSize,
        bookCount: bookIds.size,
        oldestDownload,
        newestDownload
      };

    } catch (error) {
      console.error('Get offline storage stats failed:', error);
      return {
        totalChapters: 0,
        totalSize: 0,
        bookCount: 0,
        oldestDownload: null,
        newestDownload: null
      };
    }
  }

  // ============================================================================
  // 工具方法
  // ============================================================================

  private generateBookmarkId(): string {
    return `bookmark_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateNoteId(): string {
    return `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // ============================================================================
  // 清理和销毁
  // ============================================================================

  async destroy(): Promise<void> {
    this.offlineStorage.clear();
  }
}